'use client';

import React from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { ThinkStatus as ThinkStatusType } from '@/type/chat';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import CompletedIcon from '@/components/Icons/CompletedIcon';
import { groupBy, startCase } from 'lodash';
import MarkdownContent from '@/components/markdown/MarkdownContent';
import { getLocalizedStepTitle } from '@/lib/constants/step-translations';

interface StepPanelProps {
  steps: ThinkStatusType[];
  completed?: boolean;
  queryLanguage?: string;
}

// 单个步骤项组件
const StepItem: React.FC<{ step: any; completed?: boolean; queryLanguage?: string }> = ({
  step,
  completed,
  queryLanguage,
}) => {
  // const [isExpanded, setIsExpanded] = useState(true);
  const { currentLanguage } = useTranslation();
  const stepLanguage = queryLanguage || currentLanguage;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'waiting':
      case 'pending':
        return <Clock size={16} className="text-yellow-500" />;
      case 'doing':
      case 'in_progress':
        return <Loader2 size={16} className="animate-spin text-green-500" />;
      case 'done':
      case 'completed':
        return <CompletedIcon size={14} />;
      case 'error':
        return <div className="w-3.5 h-3.5 rounded-full bg-red-500" />;
      default:
        return <Clock size={14} className="text-text-secondary" />;
    }
  };

  // try {
  //   if (step.task === 'web_search') {
  //     if (step.metadata.output_data.content) {
  //       console.log('web search step:', JSON.parse(step.metadata.output_data.content));
  //     } else {
  //       console.log('web search step:', JSON.parse(step.metadata.output_data[0].text));
  //     }
  //   }
  // } catch {}
  // 使用统一的解析函数
  // const parsedData = parseThinkingData(
  //   step.message,
  //   step.userFriendlyInfo,
  //   step.thinkingContent,
  //   step.visualization,
  // );

  // console.log('xxx', parsedData)

  // const hasExpandableContent = checkExpandable(parsedData);

  return (
    <div className="w-full flex items-start gap-2 relative">
      {/* Status Icon */}
      <div className="shrink-0 mt-0.5 z-10 bg-bg-main">
        {getStatusIcon(completed ? 'completed' : step.status)}
      </div>

      {/* Step Content */}
      <div className="flex-1 flex flex-col max-w-[738px]">
        {/* Step Title with Expand */}
        <div
          className={cn('flex items-start')}
          // onClick={() => hasExpandableContent && setIsExpanded(!isExpanded)}
        >
          {/* Step Message as Title with inline Expand Icon */}
          <div className="flex items-center gap-1 ">
            <span className="block text-[14px] text-text-primary font-medium leading-[20px] overflow-hidden text-ellipsis max-w-[600px] h-5 whitespace-nowrap">
              {getLocalizedStepTitle(step.agent, step.task, stepLanguage)}
            </span>

            {/* Expand Icon - 紧接着title */}
            {/* {hasExpandableContent && (
              <ExpandIcon
                className={cn(
                  'transition-transform duration-200 shrink-0',
                  isExpanded ? 'rotate-180' : '',
                )}
              />
            )} */}
          </div>
        </div>

        {/* Expanded Details */}
        {/* {isExpanded && hasExpandableContent && (
          <div className="mt-3 text-xs text-text-secondary">
            <StepContent parsedData={parsedData} step={step} />
          </div>
        )} */}
        {step.task === 'write_todos' && (
          <div className="ml-4 flex flex-col gap-2 mt-2 mb-6">
            {(step.metadata.output_data?.todos || step.metadata.input?.todos || []).map(
              (todo: any) => (
                <div key={todo.id} className="flex items-center gap-2 leading-5">
                  <div>{getStatusIcon(completed ? 'completed' : todo.status)}</div>
                  <div className="text-text-secondary">{todo.content}</div>
                </div>
              ),
            )}
          </div>
        )}
        {/* 其他任务类型的展示 */}
        {step.task !== 'write_todos' && (
          <>
            {step.task === 'thinking' && (
              <div className="ml-4 mt-2 mb-6 text-text-secondary">
                <MarkdownContent children={step.content || ''} selectable={false} />
              </div>
            )}
            {step.task !== 'thinking' && (
              <div className="ml-4 mt-2 mb-6 text-text-secondary line-clamp-6">
                {step.description || step.content || ''}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const StepPanel: React.FC<StepPanelProps> = ({ steps, completed, queryLanguage }) => {
  // console.log('MessageSteps', groupBy(steps, 'agent'));
  const { t } = useTranslation();
  const stepEntries = Object.entries(groupBy(steps, 'agent'));

  return (
    <div className="w-full flex flex-col text-text-primary">
      {/* Steps List */}
      <div className="relative">
        {/* 统一的垂直线 - 覆盖整个步骤区域 */}
        {(stepEntries.length > 0 || (completed && steps.length > 0)) && (
          <div
            className="absolute left-[7px] top-[7px] w-px bg-[#E9E9E9] dark:bg-[#2c2c2f]"
            style={{ height: 'calc(100% - 7px)' }}
          />
        )}

        {/* Steps - 移除内部垂直线 */}
        <div className="flex flex-col gap-6">
          {stepEntries.map(([agent, thinkStatus]) => {
            return (
              <div key={agent} className="relative">
                {thinkStatus.map((step: any, index) => (
                  <StepItem
                    key={step.toolCallId || `${agent}-${index}`}
                    step={step}
                    completed={completed}
                    queryLanguage={queryLanguage}
                  />
                ))}
              </div>
            );
          })}

          {/* 添加完成标志 */}
          {completed && steps.length > 0 && (
            <div className="relative">
              <div className="w-full flex items-start gap-2 relative">
                {/* Status Icon */}
                <div className="shrink-0 mt-0.5 z-10 bg-bg-main">
                  <CompletedIcon size={14} />
                </div>
                {/* Completion Message */}
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center gap-1">
                    <span className="block text-[14px] text-text-primary font-medium leading-[20px]">
                      {t('Analysis Complete')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepPanel;
